import React, { useState, useEffect, useRef } from 'react';
import { RotateCw, Upload, FileText, X } from './Icons';
import { Button } from './ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';

interface PDFViewerProps {
  file: File | null;
  isCollapsed: boolean;
  onFileSelect: (file: File) => void;
  onProcessStart: () => void;
  isProcessing: boolean;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ 
  file, 
  isCollapsed, 
  onFileSelect, 
  onProcessStart, 
  isProcessing 
}) => {
  const [pdfUrl, setPdfUrl] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (file) {
      // Create a blob URL for the PDF file
      const url = URL.createObjectURL(file);
      setPdfUrl(url);
      setError('');
      
      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPdfUrl('');
    }
  }, [file]);

  const handleFileSelect = (selectedFile: File) => {
    if (selectedFile.type === 'application/pdf') {
      onFileSelect(selectedFile);
    } else {
      alert('Please select a PDF file');
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
    e.target.value = '';
  };

  const clearFile = () => {
    onFileSelect(null as any);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  if (isCollapsed) {
    return (
      <div className="h-full bg-gray-100 flex flex-col items-center py-4">
        <div className="text-xs text-gray-600 writing-mode-vertical">PDF Viewer</div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-gray-800 flex flex-col transition-colors duration-200">
      {/* Header */}
      <div className="p-1 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 transition-colors duration-200">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 transition-colors duration-200">PDF Document</h3>
        
        {file && (
          <div className="flex items-center justify-between mt-3 p-2">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 bg-red-50 dark:bg-red-900/30 rounded flex items-center justify-center transition-colors duration-200">
                <FileText className="w-4 h-4 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <div className="text-xs font-medium text-gray-900 dark:text-gray-100 transition-colors duration-200">{file.name}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-200">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isProcessing && (
                <>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={clearFile}
                    className="h-6 w-6"
                    title="Remove file"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                  <Button
                    onClick={onProcessStart}
                    size="sm"
                    className="text-xs"
                  >
                    Process
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* PDF Content */}
      <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-900 transition-colors duration-200">
        {!file && (
          <div
            className={`h-full flex items-center justify-center border-2 border-dashed m-4 rounded-lg transition-colors ${
              dragOver ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' : 'border-gray-300 dark:border-gray-600'
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <div className="text-center p-6">
              <Upload className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-3 transition-colors duration-200" />
              <p className="text-sm text-gray-600 dark:text-gray-300 mb-3 transition-colors duration-200">
                Drop your invoice PDF here or click to select
              </p>
              <Button
                type="button"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose File
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                onChange={handleFileInputChange}
                className="hidden"
              />
            </div>
          </div>
        )}

        {file && error && (
          <div className="flex items-center justify-center h-full text-red-500 dark:text-red-400 transition-colors duration-200">
            <div className="text-center">
              <div className="text-4xl mb-2">❌</div>
              <div>{error}</div>
            </div>
          </div>
        )}

        {file && pdfUrl && !error && (
          <iframe
            src={pdfUrl}
            className="w-full h-full border-0 dark:opacity-90 dark:contrast-125 dark:brightness-90 transition-all duration-200"
            title="PDF Viewer"
            onError={() => setError('Failed to load PDF')}
            style={{
              filter: 'contrast(1.1) brightness(0.95)',
            }}
          />
        )}
      </div>
    </div>
  );
};